import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Button, DatePicker, Input, Progress, Select, Table, Tag, Tooltip, message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { listCustomerImportJobs } from '../../services/customerImportApi';
import { STATUS_META, computeOverallPercent } from './CustomerImportProgressPanel';

const { RangePicker } = DatePicker;

const ACTIVE_JOB_STATUSES = ['queued', 'running'];
const ACTIVE_POLL_INTERVAL_MS = 3000;
const SEARCH_DEBOUNCE_MS = 350;
const DEFAULT_PAGE_SIZE = 10;

const PHASE_LABELS = {
  customers: 'Customers',
  billtos: 'Bill-To Addresses',
  addresses: 'Ship-To Addresses',
  contacts: 'Contacts',
  indexing: 'Indexing',
  done: 'Done',
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

// Job List UI for the async customer-data import jobs (startCustomerImportJob
// in CustomerDataImport.js) -- a page-level history/monitoring view, separate
// from CustomerImportProgressPanel which only shows the single job the
// current page just started. Rows for jobs still in flight (queued/running)
// are kept fresh by re-fetching the current page on a short interval;
// polling stops automatically once nothing on the page is active, so an
// admin just browsing history doesn't generate background traffic.
export default function CustomerImportJobsPanel({ sites = [], refreshKey = 0 }) {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]); // ['YYYY-MM-DD', 'YYYY-MM-DD']
  const [siteId, setSiteId] = useState(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const searchDebounceRef = useRef(null);
  const pollRef = useRef(null);

  const siteNameById = useMemo(
    () => Object.fromEntries(sites.map((s) => [s.id, s.name])),
    [sites],
  );

  const fetchJobs = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const [dateFrom, dateTo] = dateRange;
      const result = await listCustomerImportJobs({
        site_id: siteId,
        search,
        date_from: dateFrom,
        date_to: dateTo,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setJobs(result?.jobs || []);
      setTotal(result?.total || 0);
    } catch (err) {
      if (!background) message.error(err.message || 'Could not load import jobs.');
    } finally {
      if (!background) setLoading(false);
    }
  }, [siteId, search, dateRange, page, pageSize]);

  // Any filter/page change (including an external refreshKey bump right
  // after a new job is started) re-fetches immediately.
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, search, dateRange, page, pageSize, refreshKey]);

  const hasActiveJob = useMemo(
    () => jobs.some((j) => ACTIVE_JOB_STATUSES.includes(j.status)),
    [jobs],
  );

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!hasActiveJob) return undefined;
    pollRef.current = setInterval(() => {
      fetchJobs({ background: true });
    }, ACTIVE_POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hasActiveJob, fetchJobs]);

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!value.trim()) {
      setPage(1);
      setSearch('');
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleDateRangeChange = (_dates, dateStrings) => {
    setPage(1);
    setDateRange([dateStrings?.[0] || null, dateStrings?.[1] || null]);
  };

  const columns = [
    {
      title: 'Job ID',
      dataIndex: 'job_id',
      width: 120,
      render: (value) => (
        <Tooltip title={value}>
          <code>{String(value).slice(0, 8)}</code>
        </Tooltip>
      ),
    },
    {
      title: 'Site',
      dataIndex: 'site_id',
      render: (value) => siteNameById[value] || value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const meta = STATUS_META[status] || STATUS_META.queued;
        return <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Phase',
      dataIndex: 'phase',
      render: (phase) => PHASE_LABELS[phase] || phase || '—',
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 160,
      render: (_, job) => {
        const percent = computeOverallPercent(job.progress, job.phase, job.status);
        return (
          <Progress
            percent={percent}
            size="small"
            status={job.status === 'failed' ? 'exception' : (ACTIVE_JOB_STATUSES.includes(job.status) ? 'active' : 'success')}
          />
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      render: (value) => value || '—',
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      render: formatDate,
    },
    {
      title: 'Finished',
      dataIndex: 'finished_at',
      render: formatDate,
    },
    {
      title: 'Error',
      dataIndex: 'error',
      render: (value) => (value ? (
        <Tooltip title={value}>
          <span className="text-danger small text-truncate d-inline-block" style={{ maxWidth: 180 }}>{value}</span>
        </Tooltip>
      ) : '—'),
    },
  ];

  return (
    <div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-md-4">
          <label className="form-label small" htmlFor="import-jobs-search">Search</label>
          <Input
            id="import-jobs-search"
            allowClear
            placeholder="Job ID, site, or created-by email"
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label small" htmlFor="import-jobs-date-range">Date range</label>
          <RangePicker
            id="import-jobs-date-range"
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            allowClear
            onChange={handleDateRangeChange}
          />
        </div>
        <div className="col-12 col-md-3">
          <label className="form-label small" htmlFor="import-jobs-site">Site</label>
          <Select
            id="import-jobs-site"
            style={{ width: '100%' }}
            allowClear
            placeholder="All sites"
            value={siteId}
            onChange={(value) => { setSiteId(value); setPage(1); }}
            options={sites.map((s) => ({ value: s.id, label: s.name }))}
          />
        </div>
        <div className="col-12 col-md-1 d-flex justify-content-end">
          <Button icon={<ReloadOutlined />} onClick={() => fetchJobs()} loading={loading} />
        </div>
      </div>

      <Table
        rowKey="job_id"
        columns={columns}
        dataSource={jobs}
        loading={loading}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      />
    </div>
  );
}
